import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navbar";
import Footer from "../components/Footer";
import aboutStyle from "../styles/About.module.css";

import { FaLinkedin, FaQuoteLeft, FaQuoteRight } from "react-icons/fa";
import {
  FaRocket,
  FaGlobe,
  FaHandshake,
  FaTrophy,
  FaLightbulb,
  FaUsers,
  FaChartLine,
  FaAward,
  FaStar,
  FaBuilding,
  FaCode,
  FaCloudUploadAlt,
  FaPen,
  FaCheckCircle,
} from "react-icons/fa";
import { HiOutlineLightBulb, HiOutlineRocketLaunch } from "react-icons/hi2";
import { BsStarFill, BsStarHalf } from "react-icons/bs";

import heroBg from "../assests/images/newLogo.png";
import ceoImage from "../assests/images/sailesh.jpg";
import Seo from "../components/Seo";

const About = () => {
  const navigate = useNavigate();

  // Helper function to combine multiple CSS module classes
  const combineClasses = (...classes) => {
    return classes.filter(Boolean).join(" ");
  };

  // Simple intersection observer for fade-in effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(aboutStyle["visible"]);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const items = document.querySelectorAll(`.${aboutStyle["timeline-item"]}`);
    items.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const milestones = [
    {
      year: "2017",
      icon: <FaRocket />,
      title: "The Foundation",
      description:
        "Founded in Odisha with a team of 10 passionate professionals. Began our journey with Oracle ERP consulting and custom technology development.",
      highlight: "Company Founded",
    },
    {
      year: "2018",
      icon: <FaCode />,
      title: "Building Expertise",
      description:
        "Strengthened our technical capabilities in Oracle EBS and custom application development. Established key processes and delivery frameworks.",
      highlight: "Technical Excellence",
    },
    {
      year: "2019",
      icon: <FaBuilding />,
      title: "Expansion to Raipur",
      description:
        "Established a dedicated office in Raipur, serving as a key delivery and operations hub with specialized teams.",
      highlight: "New Operations Hub",
    },
    {
      year: "2020",
      icon: <FaHandshake />,
      title: "Strategic Partnerships",
      description:
        "Expanded presence across India and formed strategic partnerships with Oracle and Zoho. Embraced compliance automation.",
      highlight: "Oracle & Zoho Partner",
    },
    {
      year: "2021",
      icon: <FaCloudUploadAlt />,
      title: "Digital Transformation",
      description:
        "Accelerated cloud adoption services and helped organizations navigate the digital-first era with comprehensive solutions.",
      highlight: "Cloud Solutions",
    },
    {
      year: "2022",
      icon: <FaGlobe />,
      title: "Going Global",
      description:
        "Entered international markets with partnerships across KSA, Singapore, Malaysia, United States, and Canada.",
      highlight: "International Expansion",
    },
    {
      year: "2023",
      icon: <FaChartLine />,
      title: "Scaling New Heights",
      description:
        "Grew our professional network to 40+ skilled experts. Enhanced our service offerings in ERP and compliance automation.",
      highlight: "40+ Professionals",
    },
    {
      year: "2024",
      icon: <FaTrophy />,
      title: "Century of Success",
      description:
        "Successfully delivered 100+ live projects, serving 50+ enterprise clients worldwide with recognized excellence.",
      highlight: "100+ Projects Delivered",
    },
    {
      year: "2025",
      icon: <FaAward />,
      title: "Industry Recognition",
      description:
        "Strengthened our position as a leading tax and technology integrator across multiple regions.",
      highlight: "Industry Leader",
    },
    {
      year: "2026",
      icon: <FaStar />,
      title: "Present & Beyond",
      description:
        "Continuing our journey with AI-driven solutions and advanced compliance automation for a connected future.",
      highlight: "The Journey Continues",
      isCurrent: true,
    },
  ];

  const whyChooseItems = [
    { icon: "✔", text: "Enterprise-grade Cloud & ERP expertise" },
    { icon: "✔", text: "Customer-first delivery approach" },
    { icon: "✔", text: "Secure, scalable, and future-ready solutions" },
    { icon: "✔", text: "Experienced consultants & engineers" },
    { icon: "✔", text: "Long-term partnership mindset" },
  ];

  return (
    <>
        <Seo path="/about" />
      <Navigation />

      {/* HERO SECTION */}
      <section
        className={combineClasses(
          aboutStyle["about-hero"],
          aboutStyle["hero-offset"]
        )}
        style={{
          backgroundImage: `linear-gradient(
      rgba(30, 96, 105, 0.92),
      rgba(30, 96, 105, 0.92)
    )`,
        }}
      >
        <div className={aboutStyle["about-container"]}>
          <h1>About Cloudare Technologies</h1>
          <p>
            Empowering businesses through innovative cloud, ERP, and enterprise
            technology solutions.
          </p>
        </div>
      </section>

      {/* THE CLOUDARE STORY */}
      <section
        className={combineClasses(
          aboutStyle["about-section"],
          aboutStyle["story-section"]
        )}
      >
        <div className={aboutStyle["about-container"]}>
          <div className={aboutStyle["story-header"]}>
            <span className={aboutStyle["section-tag"]}>Our Journey</span>
            <h2>The Cloudare Story</h2>
            <div className={aboutStyle["story-tagline"]}>
              <FaLightbulb className={aboutStyle["tagline-icon"]} />
              <span>Every successful organization begins with a vision.</span>
            </div>
          </div>

          <div className={aboutStyle["story-content"]}>
            <div className={aboutStyle["story-intro"]}>
              <p>
                Founded in <strong>2017</strong>, Cloudare Technologies was
                built on a simple yet powerful idea — to help businesses grow
                with the right blend of{" "}
                <strong>technology, talent, and trust</strong>. What began as a
                small team of passionate professionals in Odisha has evolved
                into a fast-growing IT and compliance solutions company with a
                global footprint.
              </p>
              <p>
                In its early days, Cloudare focused on building strong
                foundations. With a team of just 10 dedicated members, the
                company took its first steps into Oracle ERP consulting and
                custom technology development. The mission was clear:{" "}
                <em>
                  deliver quality, stay ethical, and always put clients first
                </em>
                .
              </p>
            </div>

            {/* Premium Timeline */}
            <div className={aboutStyle["timeline-section"]}>
              <div className={aboutStyle["timeline-header"]}>
                <h3>Our Journey Through The Years</h3>
              </div>

              <div className={aboutStyle["premium-timeline"]}>
                <div className={aboutStyle["timeline-track"]}></div>

                {milestones.map((milestone, index) => (
                  <div
                    className={combineClasses(
                      aboutStyle["timeline-item"],
                      index % 2 === 0
                        ? aboutStyle["left"]
                        : aboutStyle["right"],
                      milestone.isCurrent ? aboutStyle["current"] : ""
                    )}
                    key={index}
                    style={{ "--index": index }}
                  >
                    <div className={aboutStyle["timeline-connector"]}>
                      <div className={aboutStyle["timeline-node"]}>
                        <span className={aboutStyle["node-icon"]}>
                          {milestone.icon}
                        </span>
                      </div>
                      <span className={aboutStyle["timeline-year"]}>
                        {milestone.year}
                      </span>
                    </div>

                    <div className={aboutStyle["timeline-card"]}>
                      <span className={aboutStyle["card-highlight"]}>
                        {milestone.highlight}
                      </span>
                      <h4>{milestone.title}</h4>
                      <p>{milestone.description}</p>
                      {milestone.isCurrent && (
                        <div className={aboutStyle["current-badge"]}>
                          <span className={aboutStyle["pulse"]}></span>
                          Present
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Values Section */}
            <div className={aboutStyle["story-values-section"]}>
              <div className={aboutStyle["values-header"]}>
                <h3>Our Values Remain Unchanged</h3>
              </div>

              <div className={aboutStyle["values-content"]}>
                <p>
                  Yet, success has never changed our core values.{" "}
                  <strong>
                    Integrity, collaboration, and customer-centricity
                  </strong>{" "}
                  remain at the heart of every engagement. We continue to invest
                  in learning, innovation, and partnerships — ensuring every
                  solution we deliver is scalable, secure, and future-ready.
                </p>

                <div className={aboutStyle["values-grid"]}>
                  <div className={aboutStyle["value-card"]}>
                    <div className={aboutStyle["value-icon"]}>🤝</div>
                    <h4>Integrity</h4>
                    <p>Honest, transparent, and ethical in every interaction</p>
                  </div>
                  <div className={aboutStyle["value-card"]}>
                    <div className={aboutStyle["value-icon"]}>👥</div>
                    <h4>Collaboration</h4>
                    <p>Working together to achieve shared success</p>
                  </div>
                  <div className={aboutStyle["value-card"]}>
                    <div className={aboutStyle["value-icon"]}>🎯</div>
                    <h4>Customer-Centricity</h4>
                    <p>Putting client needs at the center of everything</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Story Highlight */}
            <div className={aboutStyle["story-highlight"]}>
              <div className={aboutStyle["highlight-content"]}>
                <p>
                  Today, Cloudare Technologies is more than an IT services
                  provider. It is a <strong>growth partner</strong>. A{" "}
                  <strong>problem solver</strong>. A{" "}
                  <strong>bridge between technology and compliance</strong>.
                </p>
              </div>
            </div>

            <div className={aboutStyle["story-closing"]}>
              <p>
                From a startup in Odisha to a global organization with a strong
                operational hub in Raipur, the Cloudare story is one of{" "}
                <em>perseverance, purpose, and progress</em>. And this story is
                still being written — together with our clients, partners, and
                people who believe in building a smarter, stronger, and more
                connected future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPANY OVERVIEW */}
      <section
        className={combineClasses(
          aboutStyle["about-section"],
          aboutStyle["light-bg"]
        )}
      >
        <div className={aboutStyle["about-container"]}>
          <h2>Who We Are</h2>
          <p>
            <strong>Cloudare Technologies</strong> is a technology-driven
            organization focused on delivering high-quality enterprise solutions
            across Cloud, ERP, and Digital Transformation platforms. We help
            businesses streamline operations, improve efficiency, and scale with
            confidence.
          </p>
          <p>
            Our expertise spans across{" "}
            <strong>
              Oracle Fusion, Oracle EBS, NetSuite, Zoho, Cloud Integrations, and
              Custom Application Development
            </strong>
            . We combine deep domain knowledge with modern engineering practices
            to deliver secure, scalable, and future-ready solutions.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section
        className={combineClasses(
          aboutStyle["about-section"],
          aboutStyle["vision-mission-section"]
        )}
      >
        <div className={aboutStyle["about-container"]}>
          <div
            className={combineClasses(
              aboutStyle["section-header"],
              aboutStyle["centered"]
            )}
          >
            <span className={aboutStyle["section-tag"]}>Our Purpose</span>
            <h2>Vision & Mission</h2>
          </div>

          <div className={aboutStyle["vision-mission-grid"]}>
            <div
              className={combineClasses(
                aboutStyle["vision-mission-card"],
                aboutStyle["vision-card"]
              )}
            >
              <div className={aboutStyle["vm-icon-wrapper"]}>
                <div className={aboutStyle["vm-icon"]}>
                  <HiOutlineLightBulb />
                </div>
              </div>
              <div className={aboutStyle["vm-content"]}>
                <h3>Our Vision</h3>
                <p>
                  To be a trusted global technology partner enabling
                  organizations to innovate, transform, and grow through
                  intelligent digital solutions.
                </p>
              </div>
              <div className={aboutStyle["vm-decoration"]}></div>
            </div>

            <div
              className={combineClasses(
                aboutStyle["vision-mission-card"],
                aboutStyle["mission-card"]
              )}
            >
              <div className={aboutStyle["vm-icon-wrapper"]}>
                <div className={aboutStyle["vm-icon"]}>
                  <HiOutlineRocketLaunch />
                </div>
              </div>
              <div className={aboutStyle["vm-content"]}>
                <h3>Our Mission</h3>
                <p>
                  To deliver reliable, cost-effective, and scalable enterprise
                  solutions by combining technology excellence, domain
                  expertise, and customer-first approach.
                </p>
              </div>
              <div className={aboutStyle["vm-decoration"]}></div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER SECTION */}
      <section
        className={combineClasses(
          aboutStyle["about-section"],
          aboutStyle["founder-section"],
          aboutStyle["light-bg"]
        )}
      >
        <div className={aboutStyle["about-container"]}>
          <div
            className={combineClasses(
              aboutStyle["section-header"],
              aboutStyle["centered"]
            )}
          >
            <span className={aboutStyle["section-tag"]}>Leadership</span>
            <h2>Know About Our Founder</h2>
          </div>

          <div className={aboutStyle["ceo-executive-card"]}>
            <div className={aboutStyle["ceo-executive-image"]}>
              <img src={ceoImage} alt="Sailesh Arya - CEO & Founder" />
              <a
                href="https://www.linkedin.com/in/sailesharya/"
                target="_blank"
                rel="noopener noreferrer"
                className={aboutStyle["ceo-linkedin"]}
              >
                <FaLinkedin />
              </a>
            </div>

            <div className={aboutStyle["ceo-executive-content"]}>
              <h3>Sailesh Arya</h3>
              <p className={aboutStyle["ceo-title"]}>
                Founder & CEO, Cloudare Technologies
              </p>

              <p>
                <strong>Sailesh Arya</strong> is the Founder and Chief Executive
                Officer of Cloudare Technologies and a seasoned Oracle Architect
                with deep expertise in enterprise systems, compliance
                automation, and digital transformation. A visionary entrepreneur
                and technology leader, he has been instrumental in shaping
                Cloudare into a trusted global partner for ERP, e-invoicing, and
                talent solutions.
              </p>

              <p>
                A 2015 graduate in Computer Science and Engineering from CET
                (OUAT), Bhubaneswar, Sailesh began his professional journey with
                leading consulting firms including{" "}
                <strong>Accenture, Deloitte, and KPMG</strong>. During this
                time, he developed strong capabilities in enterprise
                architecture, Oracle platforms, and large-scale business
                systems—experience that continues to guide Cloudare's technical
                excellence.
              </p>

              <p>
                Hailing from Kantabanji, a small town in Odisha, Sailesh founded
                Cloudare Technologies in 2017 with a clear vision: to build a
                company driven by{" "}
                <strong>integrity, innovation, and customer success</strong>.
                Starting with staffing services and small-scale support
                projects, he steadily expanded the organization's scope and
                capabilities.
              </p>

              {/*
                EDITABLE: add Sailesh's professional background before Cloudare
                here — e.g. years in Oracle ERP consulting, notable prior
                employers/roles, and key domains. Left intentionally factual and
                generic until exact details are confirmed, to avoid unverified
                claims.
              */}
              <p>
                Before founding Cloudare, Sailesh spent several years working
                hands-on across Oracle ERP implementations and enterprise
                technology delivery, an experience that shaped Cloudare's
                engineering-led, compliance-aware approach to building systems.
              </p>

              <p>
                Today, from its operational hub in Raipur, Sailesh leads a
                multidisciplinary team of developers, consultants, and support
                professionals delivering solutions across{" "}
                <strong>
                  India, the USA, Canada, Malaysia, Singapore, the UAE, and
                  Saudi Arabia
                </strong>
                . With over 40 skilled professionals and partnerships with
                industry leaders, Cloudare continues to enable businesses with
                scalable, compliant, and future-ready systems.
              </p>

              <p>
                As a hands-on leader and Oracle domain expert, Sailesh remains
                deeply involved in solution architecture, client engagements,
                and strategic growth initiatives. His leadership philosophy
                centers on building long-term relationships, nurturing talent,
                and creating sustainable value for clients and partners alike.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER'S MESSAGE */}
      <section
        className={combineClasses(
          aboutStyle["about-section"],
          aboutStyle["founder-letter-section"]
        )}
      >
        <div className={aboutStyle["about-container"]}>
          <div className={aboutStyle["letter-container"]}>
            {/* Decorative Elements */}
            <div
              className={combineClasses(
                aboutStyle["letter-decoration"],
                aboutStyle["top-left"]
              )}
            ></div>
            <div
              className={combineClasses(
                aboutStyle["letter-decoration"],
                aboutStyle["top-right"]
              )}
            ></div>
            <div
              className={combineClasses(
                aboutStyle["letter-decoration"],
                aboutStyle["bottom-left"]
              )}
            ></div>
            <div
              className={combineClasses(
                aboutStyle["letter-decoration"],
                aboutStyle["bottom-right"]
              )}
            ></div>

            {/* Letter Header */}
            <div className={aboutStyle["letter-header"]}>
              <div className={aboutStyle["letter-icon"]}>
                <FaPen />
              </div>
              <span className={aboutStyle["letter-tag"]}>A Personal Note</span>
              <h2>Founder's Message</h2>
              <div className={aboutStyle["letter-divider"]}>
                <span></span>
                <FaQuoteLeft />
                <span></span>
              </div>
            </div>

            {/* Letter Body */}
            <div className={aboutStyle["letter-body"]}>
              <p className={aboutStyle["letter-greeting"]}>
                Dear Partners, Clients & Friends,
              </p>

              <p>
                When I founded Cloudare Technologies in 2017, my vision was
                clear — to make enterprise technology simple, reliable, and
                truly valuable for businesses.
              </p>

              <p>
                Over the years, I have seen growing organizations struggle with
                complex ERP systems, evolving compliance requirements, and
                fragmented digital processes. At Cloudare, we set out to change
                this by building solutions that bring clarity, strengthen
                compliance, and support sustainable growth.
              </p>

              <p>
                Our approach is rooted in deep domain expertise, accountability,
                and long-term commitment. We do not just deliver solutions — we
                take ownership, stay engaged, and grow alongside our clients.
              </p>

              <p>
                What truly defines Cloudare is our people and our mindset. We
                listen carefully, design thoughtfully, execute with discipline,
                and support our partners well beyond implementation. Every
                engagement is built on trust, transparency, and shared success.
              </p>

              <p>
                As we expand across regions and industries, our focus remains
                unchanged: to help enterprises remain resilient, compliant, and
                future-ready in an increasingly complex digital world.
              </p>

              <p className={aboutStyle["letter-thanks"]}>
                Thank you for being part of our journey.
              </p>

              <div className={aboutStyle["letter-closing"]}>
                <span>With warm regards,</span>
              </div>
            </div>

            {/* Letter Signature */}
            <div className={aboutStyle["letter-signature"]}>
              {/* <div className={aboutStyle["signature-line"]}>
                <span className={aboutStyle["handwritten-signature"]}>
                  Sailesh Arya
                </span>
              </div> */}
              <div className={aboutStyle["signature-details"]}>
                <span className={aboutStyle["signature-name"]}>
                  Sailesh Arya
                </span>
                <span className={aboutStyle["signature-role"]}>
                  Founder & CEO
                </span>
                <span className={aboutStyle["signature-company"]}>
                  Cloudare Technologies
                </span>
              </div>
              {/* <div className={aboutStyle["signature-seal"]}>
                <div className={aboutStyle["seal-inner"]}>
                  <span>EST.</span>
                  <span className={aboutStyle["seal-year"]}>2017</span>
                </div>
              </div> */}
            </div>

            {/* Letter Footer Quote */}
            <div className={aboutStyle["letter-footer-quote"]}>
              <FaQuoteRight className={aboutStyle["footer-quote-icon"]} />
              <p>
                "Building a smarter, stronger, and more connected future —
                together."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE CLOUDARE */}
      {/* <section
        className={combineClasses(
          aboutStyle["about-section"],
          aboutStyle["light-bg"],
          aboutStyle["why-choose-section"]
        )}
      >
        <div className={aboutStyle["about-container"]}>
          <div
            className={combineClasses(
              aboutStyle["section-header"],
              aboutStyle["centered"]
            )}
          >
            <span className={aboutStyle["section-tag"]}>Our Strengths</span>
            <h2>Why Choose Cloudare</h2>
          </div>

          <div className={aboutStyle["why-choose-grid"]}>
            {whyChooseItems.map((item, index) => (
              <div className={aboutStyle["why-choose-item"]} key={index}>
                <span className={aboutStyle["why-choose-icon"]}>
                  {item.icon}
                </span>
                <span className={aboutStyle["why-choose-text"]}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* LEGAL SECTION */}
      {/* <section className={aboutStyle["about-section"]}>
        <div className={aboutStyle["about-container"]}>
          <div className={aboutStyle["legal-wrapper"]}>
            <h2 className={aboutStyle["legal-title"]}>Legal & Compliance</h2>
            <p className={aboutStyle["legal-subtitle"]}>
              Transparency and trust are core to how we operate. Please review
              our policies below.
            </p>

            <div className={aboutStyle["legal-card"]}>
              <ul className={aboutStyle["legal-links"]}>
                <li>
                  <span onClick={() => navigate("/privacy-policy")}>
                    Privacy Policy
                  </span>
                </li>
                <li>
                  <span onClick={() => navigate("/terms")}>
                    Terms & Conditions
                  </span>
                </li>
                <li>
                  <span onClick={() => navigate("/refund-policy")}>
                    Refund & Cancellation Policy
                  </span>
                </li>
                <li>
                  <span onClick={() => navigate("/blacklist")}>
                    Blacklisted Company Disclosure
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section> */}

      {/* RATINGS & REVIEWS */}
      {/* <section
        className={combineClasses(
          aboutStyle["about-section"],
          aboutStyle["light-bg"],
          aboutStyle["ratings-section"]
        )}
      >
        <div className={aboutStyle["about-container"]}>
          <div className={aboutStyle["ratings-container"]}>
            <div
              className={combineClasses(
                aboutStyle["section-header"],
                aboutStyle["centered"]
              )}
            >
              <span className={aboutStyle["section-tag"]}>What People Say</span>
              <h2>Ratings & Reviews</h2>
              <p className={aboutStyle["ratings-description"]}>
                Our workplace culture and delivery standards are reflected in
                the feedback shared by our team and professionals across
                platforms.
              </p>
            </div>

            <div className={aboutStyle["ratings-content"]}>
              <div className={aboutStyle["ratings-stats"]}>
                <div className={aboutStyle["stat-card"]}>
                  <div className={aboutStyle["stat-icon"]}>
                    <FaUsers />
                  </div>
                  <div className={aboutStyle["stat-info"]}>
                    <span className={aboutStyle["stat-number"]}>40+</span>
                    <span className={aboutStyle["stat-label"]}>
                      Team Members
                    </span>
                  </div>
                </div>

                <div className={aboutStyle["stat-card"]}>
                  <div className={aboutStyle["stat-icon"]}>
                    <FaTrophy />
                  </div>
                  <div className={aboutStyle["stat-info"]}>
                    <span className={aboutStyle["stat-number"]}>100+</span>
                    <span className={aboutStyle["stat-label"]}>
                      Projects Delivered
                    </span>
                  </div>
                </div>

                <div className={aboutStyle["stat-card"]}>
                  <div className={aboutStyle["stat-icon"]}>
                    <FaGlobe />
                  </div>
                  <div className={aboutStyle["stat-info"]}>
                    <span className={aboutStyle["stat-number"]}>7+</span>
                    <span className={aboutStyle["stat-label"]}>
                      Countries Served
                    </span>
                  </div>
                </div>
              </div>

              <div className={aboutStyle["ratings-badge-wrapper"]}>
                <div className={aboutStyle["ratings-badge-card"]}>
                  <div className={aboutStyle["badge-header"]}>
                    <div className={aboutStyle["stars-display"]}>
                      <BsStarFill />
                      <BsStarFill />
                      <BsStarFill />
                      <BsStarFill />
                      <BsStarHalf />
                    </div>
                    <span className={aboutStyle["badge-title"]}>
                      Rated on AmbitionBox
                    </span>
                  </div>

                  <div className={aboutStyle["badge-content"]}>
                    <a
                      href="https://www.ambitionbox.com/overview/cloudare-technologies-overview?utm_source=employer-dashboard&utm_campaign=cloudare-technologies&utm_medium=badges"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={aboutStyle["ratings-badge-link"]}
                    >
                      <img
                        src="https://employer.ambitionbox.com/api/badge/917985?badge-type=ratings-detailed"
                        alt="Cloudare Technologies Ratings on AmbitionBox"
                      />
                    </a>
                  </div>

                  <div className={aboutStyle["badge-footer"]}>
                    <FaCheckCircle className={aboutStyle["verified-icon"]} />
                    <span>Verified Company Profile</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      <Footer />
    </>
  );
};

export default About;
